import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, MapPin, Globe, Award, Shield } from 'lucide-react';

const Footer = () => {
  const { t, isRTL } = useLanguage();

  return (
    <footer className='bg-gray-900 text-gray-300'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
        {/* Main Footer Content */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-8'>
          {/* Company Info */}
          <div className={`lg:col-span-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img 
                src='logo.png?v=1.1' 
                alt={t('companyName')} 
                className='w-12 h-12 object-contain' 
              />
              <div>
                <h3 className={`text-xl font-bold text-white ${isRTL ? 'font-arabic' : 'font-english'}`}>
                  {t('companyName')}
                </h3>
                <p className={`text-sm text-gray-400 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                  {t('companyTagline')}
                </p>
              </div>
            </div>
            <p className={`text-gray-400 leading-relaxed mb-4 ${isRTL ? 'font-arabic text-right' : 'font-english'}`}>
              {t('footerDescription') || 'Kuwait Chemicals provides high-quality medical products and pharmaceutical supplies with advanced verification technology for healthcare professionals.'}
            </p>
            <div className={`flex items-center gap-2 text-primary-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Award size={16} />
              <span className={`text-sm ${isRTL ? 'font-arabic' : 'font-english'}`}>
                {t('isoCompliant') || 'ISO 9001:2015 Certified'}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h3 className={`text-lg font-bold text-white mb-4 ${isRTL ? 'font-arabic' : 'font-english'}`}>
              {t('quickLinks')}
            </h3>
            <ul className='space-y-3 text-sm'>
              {[
                { to: '/', label: t('home') },
                { to: '/about', label: t('about') },
                { to: '/privacy', label: t('privacyPolicy') },
                { to: '/terms', label: t('termsConditions') },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`text-gray-400 hover:text-primary-400 transition-colors ${isRTL ? 'font-arabic' : 'font-english'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Categories */}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h3 className={`text-lg font-bold text-white mb-4 ${isRTL ? 'font-arabic' : 'font-english'}`}>
              {t('productCategories') || 'Product Categories'}
            </h3>
            <ul className='space-y-3 text-sm text-gray-400'>
              <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                <Shield size={14} className="text-primary-400" />
                {t('medicalDevices') || 'Medical Devices'}
              </li>
              <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                <Shield size={14} className="text-primary-400" />
                {t('pharmaceuticals') || 'Pharmaceuticals'}
              </li>
              <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                <Shield size={14} className="text-primary-400" />
                {t('laboratorySupplies') || 'Laboratory Supplies'}
              </li>
              <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                <Shield size={14} className="text-primary-400" />
                {t('diagnosticEquipment') || 'Diagnostic Equipment'}
              </li>
              <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                <Shield size={14} className="text-primary-400" />
                {t('surgicalInstruments') || 'Surgical Instruments'}
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h3 className={`text-lg font-bold text-white mb-4 ${isRTL ? 'font-arabic' : 'font-english'}`}>
              {t('contactInfo')}
            </h3>
            <ul className='space-y-4 text-sm text-gray-400'>
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Mail size={16} className="text-primary-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className={isRTL ? 'font-arabic' : 'font-english'}>info@kuwait-chemicals.com</p>
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                    {t('generalInquiries') || 'General Inquiries'}
                  </p>
                </div>
              </li>
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Phone size={16} className="text-primary-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p>+965 2XXX XXXX</p>
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                    {t('businessHours') || 'Sun-Thu: 8AM-5PM'}
                  </p>
                </div>
              </li>
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <MapPin size={16} className="text-primary-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className={isRTL ? 'font-arabic' : 'font-english'}>
                    {t('address') || 'Kuwait City, Kuwait'}
                  </p>
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                    {t('headquarters') || 'Headquarters'}
                  </p>
                </div>
              </li>
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Globe size={16} className="text-primary-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className={isRTL ? 'font-arabic' : 'font-english'}>
                    {t('globalReach') || '50+ Countries Served'}
                  </p>
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                    {t('worldwide') || 'Worldwide Distribution'}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Certifications */}
        <div className='border-t border-gray-700 pt-6 mb-6'>
          <div className={`text-center ${isRTL ? 'font-arabic' : 'font-english'}`}>
            <h4 className='text-white font-semibold mb-3'>
              {t('certifications') || 'Certifications & Compliance'}
            </h4>
            <div className={`flex flex-wrap justify-center gap-6 text-sm text-gray-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{t('iso9001') || 'ISO 9001:2015'}</span>
              <span>{t('iso13485') || 'ISO 13485'}</span>
              <span>{t('ceMarking') || 'CE Marking'}</span>
              <span>{t('fdaApproved') || 'FDA Approved'}</span>
              <span>{t('gmpCertified') || 'GMP Certified'}</span>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className='border-t border-gray-700 pt-6 text-center'>
          <p className={`text-sm text-gray-400 ${isRTL ? 'font-arabic' : 'font-english'}`}>
            &copy; {new Date().getFullYear()}{' '}
            <span className='text-primary-400'>{t('companyName')}</span>. {t('allRightsReserved')}
          </p>
          <p className={`text-xs text-gray-500 mt-2 ${isRTL ? 'font-arabic' : 'font-english'}`}>
            {t('qualityMedicine') || 'Committed to Quality Medicine & Healthcare Excellence'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
